import { useState, useEffect, useRef, useMemo, Fragment } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@services/auth';
import { getShowcaseProjects, type ShowcaseProject } from '@data/showcaseProjects';
import { stylePresets, type StylePreset } from '@data/stylePresets';
import BeforeAfterCarousel from '@components/public/BeforeAfterCarousel';
import GardenStyleQuiz from '@components/public/GardenStyleQuiz';
import { useI18n } from '../../i18n';
import { useCountUp } from '@utils/useCountUp';
import { usePageActivityTracker } from '@services/activityTracker';

const SHOW_COMING_SOON = false;

type DualHowItWorksStep = {
	image?: string;
	imageAlt?: string;
	seeker: { icon: string; title: string; description: string };
	company: { icon: string; title: string; description: string };
};

const DUAL_STEP_IMAGE_KEYS: StylePreset['key'][] = ['modern', 'mediterranean', 'traditional'];

function UnifiedHowItWorksCards({
	steps,
	visible,
	seekerLabel,
	companyLabel
}: {
	steps: DualHowItWorksStep[];
	visible: boolean;
	seekerLabel: string;
	companyLabel: string;
}) {
	return (
		<div className="how-dual-flow">
			{steps.map((step, index) => (
				<Fragment key={step.seeker.title}>
					{index > 0 ? (
						<div className="how-dual-flow-arrow" aria-hidden>
							<i className="bi bi-arrow-right how-dual-flow-arrow-icon how-dual-flow-arrow-icon--horizontal" />
							<i className="bi bi-arrow-down how-dual-flow-arrow-icon how-dual-flow-arrow-icon--vertical" />
						</div>
					) : null}
					<div className="how-dual-flow-step">
						<div
							className={`how-dual-step-card h-100 rounded-4 border bg-white shadow-sm overflow-hidden d-flex flex-column scroll-fade-in ${visible ? 'visible' : ''}`}
							style={{
								opacity: visible ? 1 : 0,
								transform: visible ? 'translateY(0)' : 'translateY(30px)',
								transition: `opacity 0.6s ease-out ${index * 0.15}s, transform 0.6s ease-out ${index * 0.15}s`
							}}
						>
							<div className="how-dual-side how-dual-side--seeker">
								<div className="how-dual-side-label">
									<i className="bi bi-house-heart" aria-hidden />
									{seekerLabel}
								</div>
								<div className="how-dual-side-body">
									<div className="how-dual-icon how-dual-icon--seeker">
										<i className={`bi ${step.seeker.icon}`} aria-hidden />
									</div>
									<h3 className="how-dual-title">{step.seeker.title}</h3>
									<p className="how-dual-desc">{step.seeker.description}</p>
								</div>
							</div>

							{step.image ? (
								<div className="how-dual-photo">
									<img src={step.image} alt={step.imageAlt} className="how-dual-photo-img" loading="lazy" />
								</div>
							) : null}

							<div className="how-dual-side how-dual-side--company">
								<div className="how-dual-side-label">
									<i className="bi bi-building" aria-hidden />
									{companyLabel}
								</div>
								<div className="how-dual-side-body">
									<div className="how-dual-icon how-dual-icon--company">
										<i className={`bi ${step.company.icon}`} aria-hidden />
									</div>
									<h3 className="how-dual-title">{step.company.title}</h3>
									<p className="how-dual-desc">{step.company.description}</p>
								</div>
							</div>
						</div>
					</div>
				</Fragment>
			))}
		</div>
	);
}

export default function HomeLanding() {
	const { user } = useAuth();
	usePageActivityTracker('home_landing');
	const { t, language } = useI18n();
	const showcaseProjects = getShowcaseProjects(language);
	const feedbackProjects = showcaseProjects.slice(0, 3);
	const transformCarouselProjects = useMemo(() => showcaseProjects.slice(0, 3), [showcaseProjects]);
	const tryQuestionnaireHref = '/try-questionnaire';
	const localeByLanguage = { en: 'en-US', de: 'de-DE', hr: 'hr-HR' } as const;
	const copy = (en: string, de: string, hr: string) =>
		language === 'de' ? de : language === 'hr' ? hr : en;
	const navigate = useNavigate();
	const location = useLocation();
	const logoSrc = `${import.meta.env.BASE_URL}logo-transparent.png`;

	// If the homepage is opened with an invite code (?invite=CODE),
	// we pass that code forward to /login so that after login the seeker
	// lands on My Plans WITH the invite query still attached.
	const inviteCodeFromUrl = (() => {
		try {
			const params = new URLSearchParams(location.search);
			const raw = params.get('invite') || params.get('code');
			return raw && raw.trim() ? raw.trim() : null;
		} catch {
			return null;
		}
	})();

	// If homepage is opened with ?gardenerInvite=CODE, pass it forward to /login
	const gardenerInviteCodeFromUrl = (() => {
		try {
			const params = new URLSearchParams(location.search);
			const raw = params.get('gardenerInvite');
			return raw && raw.trim() ? raw.trim() : null;
		} catch {
			return null;
		}
	})();

	const seekerHomeHref = '/app/seeker/plans';
	const defaultDashboardHref = '/app/dashboard';

	const primaryCtaHref = user
		? user.role === 'seeker'
			? seekerHomeHref
			: defaultDashboardHref
		: gardenerInviteCodeFromUrl
			? `/login?gardenerInvite=${encodeURIComponent(gardenerInviteCodeFromUrl)}`
			: inviteCodeFromUrl
				? `/login?invite=${encodeURIComponent(inviteCodeFromUrl)}`
				: '/login';
	const primaryCtaLabel = user ? t('home.hero.primaryCta.user', 'Back to your dashboard') : t('home.hero.primaryCta.guest', 'Start building');

	const secondaryCtaHref = user
		? user.role === 'seeker'
			? '/app/seeker/projects'
			: '/app/projects'
		: '#process';
	const secondaryCtaLabel = user ? t('home.hero.secondaryCta.user', 'View live projects') : t('home.hero.secondaryCta.guest', 'See how it works');

	const bottomCtaHref = user
		? user.role === 'seeker'
			? seekerHomeHref
			: defaultDashboardHref
		: gardenerInviteCodeFromUrl
			? `/login?gardenerInvite=${encodeURIComponent(gardenerInviteCodeFromUrl)}`
			: inviteCodeFromUrl
				? `/login?invite=${encodeURIComponent(inviteCodeFromUrl)}`
				: '/login';
	const bottomCtaLabel = user ? t('home.cta.bottomUser', 'Return to dashboard') : t('home.cta.bottomGuest', 'Start building');
	const unifiedHowSteps = useMemo((): DualHowItWorksStep[] => {
		const stepDefs = [
			{
				seeker: {
					icon: 'bi-journal-plus',
					title: t('home.process.step1.title', 'Create your garden plan'),
					description: t(
						'home.process.step1.description',
						'Short questionnaire about your space and style. Add a photo anytime for more tailored suggestions.'
					)
				},
				company: {
					icon: 'bi-shop',
					title: t('home.pro.step1.title', 'Browse the marketplace'),
					description: t(
						'home.pro.step1.description',
						'See seeker plans with photos, style, and scope details—ready for quoting without an extra site visit.'
					)
				}
			},
			{
				seeker: {
					icon: 'bi-envelope-paper-heart',
					title: t('home.process.step2.title', 'Receive offers from gardeners'),
					description: t(
						'home.process.step2.description',
						'Compare budgets and timelines in one place when gardeners and companies respond to your plan.'
					)
				},
				company: {
					icon: 'bi-file-earmark-text',
					title: t('home.pro.step2.title', 'Send offers & quotes'),
					description: t(
						'home.pro.step2.description',
						'Build professional quotes, respond to plans, and invite seekers to start with your company.'
					)
				}
			},
			{
				seeker: {
					icon: 'bi-kanban',
					title: t('home.process.step3.title', 'Turn a plan into a project'),
					description: t(
						'home.process.step3.description',
						'Choose an offer you like and track progress—ideas, photos, and messages stay in one hub.'
					)
				},
				company: {
					icon: 'bi-people',
					title: t('home.pro.step3.title', 'Run projects & teams'),
					description: t(
						'home.pro.step3.description',
						'Track tasks, schedule crews, share progress photos, and keep clients updated in one hub.'
					)
				}
			}
		];

		return stepDefs.map((step, index) => {
			const preset = stylePresets.find((s) => s.key === DUAL_STEP_IMAGE_KEYS[index]);
			return {
				...step,
				image: preset?.imageUrl,
				imageAlt: preset ? `${preset.title} garden style` : ''
			};
		});
	}, [t]);

	const proCtaHref = user
		? defaultDashboardHref
		: gardenerInviteCodeFromUrl
			? `/login?gardenerInvite=${encodeURIComponent(gardenerInviteCodeFromUrl)}`
			: '/login';
	const gardensDesigned = Math.round(useCountUp(130));
	const gardenStyles = Math.round(useCountUp(10));
	const minutesToPlan = Math.max(1, Math.round(useCountUp(3)));

	const [selectedProject, setSelectedProject] = useState<ShowcaseProject | null>(null);
	
	// Scroll animations - check visibility on mount
	const [processVisible, setProcessVisible] = useState(false);
	const [showcaseVisible, setShowcaseVisible] = useState(false);
	const [workspaceVisible, setWorkspaceVisible] = useState(false);
	
	const processRef = useRef<HTMLElement>(null);
	const showcaseRef = useRef<HTMLElement>(null);
	const workspaceRef = useRef<HTMLElement>(null);
	
	useEffect(() => {
		const observers: IntersectionObserver[] = [];
		
		// Helper to check if element is in viewport (more lenient check)
		const isInViewport = (element: HTMLElement) => {
			const rect = element.getBoundingClientRect();
			const windowHeight = window.innerHeight || document.documentElement.clientHeight;
			const windowWidth = window.innerWidth || document.documentElement.clientWidth;
			return (
				rect.top < windowHeight * 0.8 && // Element is in top 80% of viewport
				rect.bottom > 0 &&
				rect.left < windowWidth &&
				rect.right > 0
			);
		};
		
		// Small delay to ensure DOM is ready
		const checkAndObserve = () => {
			// Process section observer
			if (processRef.current) {
				if (isInViewport(processRef.current)) {
					setProcessVisible(true);
				} else {
					const observer = new IntersectionObserver(
						([entry]) => {
							if (entry.isIntersecting) {
								setProcessVisible(true);
								observer.unobserve(entry.target);
							}
						},
						{ threshold: 0.1, rootMargin: '0px 0px -100px 0px' }
					);
					observer.observe(processRef.current);
					observers.push(observer);
				}
			}
			
			// Showcase section observer
			if (showcaseRef.current) {
				if (isInViewport(showcaseRef.current)) {
					setShowcaseVisible(true);
				} else {
					const observer = new IntersectionObserver(
						([entry]) => {
							if (entry.isIntersecting) {
								setShowcaseVisible(true);
								observer.unobserve(entry.target);
							}
						},
						{ threshold: 0.1, rootMargin: '0px 0px -100px 0px' }
					);
					observer.observe(showcaseRef.current);
					observers.push(observer);
				}
			}
			
			// Workspace section observer
			if (workspaceRef.current) {
				if (isInViewport(workspaceRef.current)) {
					setWorkspaceVisible(true);
				} else {
					const observer = new IntersectionObserver(
						([entry]) => {
							if (entry.isIntersecting) {
								setWorkspaceVisible(true);
								observer.unobserve(entry.target);
							}
						},
						{ threshold: 0.1, rootMargin: '0px 0px -100px 0px' }
					);
					observer.observe(workspaceRef.current);
					observers.push(observer);
				}
			}
		};
		
		// Check immediately and also after a small delay
		checkAndObserve();
		const timeout = setTimeout(checkAndObserve, 100);
		
		// Fallback: show all sections after 2 seconds if observer hasn't fired
		const fallbackTimeout = setTimeout(() => {
			setProcessVisible(true);
			setShowcaseVisible(true);
			setWorkspaceVisible(true);
		}, 2000);
		
		return () => {
			observers.forEach(obs => obs.disconnect());
			clearTimeout(timeout);
			clearTimeout(fallbackTimeout);
		};
	}, []);

	const handleTransformationClick = (projectId: string) => {
		const project = showcaseProjects.find(p => p.id === projectId);
		if (project) {
			setSelectedProject(project);
		}
	};

	const handleCloseModal = () => {
		setSelectedProject(null);
	};

	const handleGoToGallery = () => {
		handleCloseModal();
		// Navigate to gallery without project parameter so modal doesn't auto-open
		setTimeout(() => {
			navigate('/gallery');
		}, 100);
	};

	// Handle ESC key to close modal and body scroll lock
	useEffect(() => {
		if (selectedProject) {
			// Prevent body scroll when modal is open
			document.body.style.overflow = 'hidden';
			
			// Handle ESC key
			const handleEsc = (e: KeyboardEvent) => {
				if (e.key === 'Escape') {
					handleCloseModal();
				}
			};
			document.addEventListener('keydown', handleEsc);
			
			return () => {
				document.body.style.overflow = '';
				document.removeEventListener('keydown', handleEsc);
			};
		}
	}, [selectedProject]);

	return (
		<>
			<section id="hero" className="landing-hero py-5 py-lg-5">
				<div className="container">
					<div className="row align-items-center g-5">
						<div className="col-12 col-lg-6 order-lg-2">
							<div className="hero-garden-image rounded-4 overflow-hidden shadow-lg">
								<img 
									src={`${import.meta.env.BASE_URL}hero-garden.png`}
									alt="Idyllic cottage garden with winding stone path and vibrant flowers" 
									className="w-100"
									style={{ minHeight: 'clamp(220px, 45vh, 500px)', objectFit: 'cover', width: '100%' }}
								/>
							</div>
						</div>
						<div className="col-12 col-lg-6 order-lg-1">
							<h1 className="display-4 fw-bold text-success-emphasis mb-4">
								{t('home.hero.title', 'Transform any outdoor space into your dream garden.')}
							</h1>
							<p className="lead text-muted mb-4 mb-md-5 landing-hero-subtitle">
								{t(
									'home.hero.subtitle',
									'From vision to reality—create stunning gardens with intuitive planning tools, connect with expert gardeners, and watch your outdoor space come to life.'
								)}
							</p>
							<div className="d-flex flex-column flex-sm-row flex-wrap gap-3">
								<Link to={primaryCtaHref} className="btn btn-lg btn-success px-4 py-3 btn-ripple hover-glow">
									{primaryCtaLabel}
								</Link>
								{user ? (
									<Link className="btn btn-lg btn-outline-success px-4 py-3 hover-lift" to={secondaryCtaHref}>
										<i className="bi bi-kanban me-2"></i>
										{secondaryCtaLabel}
									</Link>
								) : (
									<a className="btn btn-lg btn-outline-success px-4 py-3 hover-lift" href={secondaryCtaHref}>
										<i className="bi bi-play-circle me-2"></i>
										{secondaryCtaLabel}
									</a>
								)}
							</div>
							<div className="landing-stats d-flex flex-wrap gap-4 mt-5">
								<div>
									<div className="h2 fw-bold text-success mb-0">{gardensDesigned}+</div>
									<div className="text-muted small">
										{copy('Gardens designed', 'Gestaltete Gärten', 'Dizajniranih vrtova')}
									</div>
								</div>
								<div>
									<div className="h2 fw-bold text-success mb-0">{gardenStyles}</div>
									<div className="text-muted small">{copy('Garden styles', 'Gartenstile', 'Vrtnih stilova')}</div>
								</div>
								<div>
									<div className="h2 fw-bold text-success mb-0">&lt;{minutesToPlan}</div>
									<div className="text-muted small">
										{copy('mins to get your plan', 'Min. bis zu Ihrem Plan', 'min do vašeg plana')}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			<section id="process" ref={processRef as React.RefObject<HTMLElement>} className="landing-section py-5">
				<div className="container">
					<div className="text-center mb-5" style={{ opacity: 1, visibility: 'visible' }}>
						<h2 className="display-6 text-success-emphasis mb-3">
							{t('home.process.unifiedTitle', 'How HortEra connects everyone')}
						</h2>
						<p className="text-muted lead fs-6 mx-auto mb-0" style={{ maxWidth: '42rem' }}>
							{t(
								'home.process.unifiedSubtitle',
								'Three steps—from first garden idea to finished project. Each step shows what garden seekers do and how gardeners & companies respond.'
							)}
						</p>
					</div>
					<UnifiedHowItWorksCards
						steps={unifiedHowSteps}
						visible={processVisible}
						seekerLabel={t('home.process.seekerLabel', 'Garden seeker')}
						companyLabel={t('home.process.companyLabel', 'Gardener or company')}
					/>
					{!user && (
						<div className="text-center mt-5">
							<div className="d-flex flex-column flex-sm-row justify-content-center gap-3">
								<Link
									to="/try-questionnaire"
									className="btn btn-outline-success btn-lg px-4 rounded-pill"
								>
									<i className="bi bi-ui-checks-grid me-2" aria-hidden />
									{t('home.process.tryQuestionnaire', 'Try the questionnaire')}
								</Link>
								<Link to={proCtaHref} className="btn btn-outline-success btn-lg px-4 rounded-pill">
									<i className="bi bi-building me-2" aria-hidden />
									{t('home.pro.cta', 'Get started as a company')}
								</Link>
							</div>
							<p className="text-muted small mt-3 mb-0">
								{t(
									'home.process.unifiedCtaHint',
									'No account needed to try the questionnaire—sign in when you are ready to save a plan or browse the marketplace.'
								)}
							</p>
						</div>
					)}
				</div>
			</section>

			<section id="transforms" className="landing-section py-5 bg-white">
				<div className="container">
					<div className="text-center mb-4">
						<div className="text-uppercase small fw-semibold text-success-emphasis opacity-75 mb-2">
							{t('home.transforms.eyebrow', 'Transformations')}
						</div>
						<h2 className="h2 text-success-emphasis mb-2">
							{t('home.transforms.title', 'See what’s possible')}
						</h2>
						<p className="text-muted mb-0">
							{t(
								'home.transforms.subtitle',
								'Drag the slider to compare before and after. Use the arrows to explore each story.'
							)}
						</p>
					</div>
					<BeforeAfterCarousel
						projects={transformCarouselProjects}
						beforeLabel={t('home.showcase.before', 'Before')}
						afterLabel={t('home.showcase.after', 'After')}
					/>
				</div>
			</section>

			<section id="style-quiz" className="landing-section py-5">
				<div className="container">
					<div className="text-center mb-4">
						<div className="text-uppercase small fw-semibold text-success-emphasis opacity-75 mb-2">
							{t('home.quiz.eyebrow', 'Discover your style')}
						</div>
						<h2 className="h2 text-success-emphasis mb-2">
							{t('home.quiz.title', 'What kind of garden are you?')}
						</h2>
						<p className="text-muted mb-4">
							{t('home.quiz.subtitle', '3 quick questions. Instant personalised result.')}
						</p>
					</div>
					<GardenStyleQuiz
						tryQuestionnaireHref={tryQuestionnaireHref}
						designCtaLabel={t('home.quiz.designCta', 'Design my garden')}
						retakeLabel={t('home.quiz.retake', 'Retake quiz')}
						yourStyleLabel={t('home.quiz.yourStyle', 'Your style is')}
						questionOfLabel={(current, total) =>
							t('home.quiz.questionOf', 'Question {current} of {total}')
								.replace('{current}', String(current))
								.replace('{total}', String(total))
						}
					/>
				</div>
			</section>

			<section id="showcase" ref={showcaseRef as React.RefObject<HTMLElement>} className="landing-section py-5 bg-white">
				<div className="container">
					<div className="row justify-content-center mb-4" style={{ opacity: 1, visibility: 'visible' }}>
						<div className="col-lg-8 text-center">
							<h2 className="display-6 text-success-emphasis mb-3" style={{ opacity: 1, visibility: 'visible' }}>
								{t('home.showcase.title', 'Garden transformations')}
							</h2>
							<p className="text-muted mb-0">
								{t(
									'home.showcase.subtitle',
									'See how any space can become a beautiful garden. Our before-and-after stories help garden seekers imagine every possibility while expert crews know exactly what to build.'
								)}
							</p>
						</div>
					</div>
					<div className="row g-3">
						{feedbackProjects.map((project, index) => (
							<div key={project.id} className="col-12 col-md-6 col-lg-4">
								<div
									role="button"
									tabIndex={0}
									className={`seeker-testimonial-card rounded-4 overflow-hidden h-100 seeker-testimonial-card--clickable scroll-fade-in ${showcaseVisible ? 'visible' : ''}`}
									style={{
										opacity: showcaseVisible ? 1 : 0,
										transform: showcaseVisible ? 'scale(1)' : 'scale(0.9)',
										transition: `opacity 0.6s ease-out ${index * 0.1}s, transform 0.6s ease-out ${index * 0.1}s`
									}}
									onClick={() => handleTransformationClick(project.id)}
									onKeyDown={(e) => {
										if (e.key === 'Enter' || e.key === ' ') {
											e.preventDefault();
											handleTransformationClick(project.id);
										}
									}}
								>
									<img src={project.after} alt={project.title} className="w-100 seeker-feedback-image" />
									<div className="p-3">
										<div className="d-flex align-items-center justify-content-between mb-2">
											<span className="badge rounded-pill text-bg-success">{project.style}</span>
											<span className="text-warning small" aria-label="5 out of 5 stars">★★★★★</span>
										</div>
										<p className="small text-muted mb-2">&ldquo;{project.details.testimonial}&rdquo;</p>
										<div className="small fw-semibold text-success-emphasis">{project.client}</div>
										<p className="text-success small mt-2 mb-0">
											<i className="bi bi-arrow-right me-1" aria-hidden />
											{t('home.showcase.details', 'View project details')}
										</p>
									</div>
								</div>
							</div>
						))}
					</div>
				</div>
			</section>

			<section id="workspace" ref={workspaceRef as React.RefObject<HTMLElement>} className="landing-section py-5 bg-light">
				<div className="container">
					<div className="text-center mb-5" style={{ opacity: 1, visibility: 'visible' }}>
						<h2 className="display-6 text-success-emphasis mb-3" style={{ opacity: 1, visibility: 'visible' }}>
							{t('home.workspace.title', 'Built for everyone who loves gardens')}
						</h2>
						<p className="text-muted lead fs-5">
							{t(
								'home.workspace.subtitle',
								"Whether you're dreaming up your perfect garden or bringing it to life, HortEra connects all the right people in one collaborative workspace."
							)}
						</p>
					</div>
					<div className="row g-4 justify-content-center">
						<div className="col-12 col-md-6 col-lg-3">
							<div className={`persona-card seeker h-100 p-4 hover-lift scroll-fade-in ${workspaceVisible ? 'visible' : ''}`} style={{ opacity: workspaceVisible ? 1 : 0, transform: workspaceVisible ? 'translateY(0)' : 'translateY(30px)', transition: 'opacity 0.6s ease-out 0.1s, transform 0.6s ease-out 0.1s' }}>
								<div className="d-flex align-items-center gap-2 mb-3">
									<i className="bi bi-house-heart fs-4 text-success"></i>
									<span className="fw-semibold h5 mb-0">{copy('Garden Seeker', 'Gartensuchende', 'Vlasnik vrta')}</span>
								</div>
								<p className="text-muted small mb-3">
									{copy('Create your garden plan, compare offers and bring your project to life.', 'Erstellen Sie Ihren Gartenplan, vergleichen Sie Angebote und bringen Sie Ihr Projekt zum Leben.', 'Izradite svoj vrtni plan, usporedite ponude i oživite svoj projekt.')}
								</p>
								<ul className="list-unstyled small text-muted mb-0">
									<li className="d-flex align-items-center gap-2 mb-2">
										<i className="bi bi-check-circle-fill text-success"></i>
										{copy('Garden questionnaire & photo upload', 'Garten-Fragebogen & Foto-Upload', 'Vrtni upitnik i prijenos fotografije')}
									</li>
									<li className="d-flex align-items-center gap-2 mb-2">
										<i className="bi bi-check-circle-fill text-success"></i>
										{copy('Design previews & style ideas', 'Design-Vorschauen & Stilideen', 'Pregledi dizajna i ideje stilova')}
									</li>
									<li className="d-flex align-items-center gap-2 mb-2">
										<i className="bi bi-check-circle-fill text-success"></i>
										{copy('Before & after visuals', 'Vorher-/Nachher-Visuals', 'Prikazi prije i poslije')}
									</li>
									<li className="d-flex align-items-center gap-2 mb-2">
										<i className="bi bi-check-circle-fill text-success"></i>
										{copy('Compare & accept offers', 'Angebote vergleichen & akzeptieren', 'Usporedi i prihvati ponude')}
									</li>
									<li className="d-flex align-items-center gap-2 mb-2">
										<i className="bi bi-check-circle-fill text-success"></i>
										{copy('Download plan PDFs', 'Plan-PDFs herunterladen', 'Preuzmi PDF planove')}
									</li>
									<li className="d-flex align-items-center gap-2 mb-2">
										<i className="bi bi-check-circle-fill text-success"></i>
										{copy('My plans & project tracking', 'Meine Pläne & Projektverfolgung', 'Moji planovi i praćenje projekta')}
									</li>
									<li className="d-flex align-items-center gap-2 mb-2">
										<i className="bi bi-check-circle-fill text-success"></i>
										{copy('Create plans from company invites', 'Pläne aus Unternehmenseinladungen erstellen', 'Kreiraj planove iz pozivnica tvrtke')}
									</li>
									<li className="d-flex align-items-center gap-2 mb-2">
										<i className="bi bi-check-circle-fill text-success"></i>
										{copy('Messages with gardeners & team', 'Nachrichten mit Gärtnern & Team', 'Poruke s vrtlarima i timom')}
									</li>
								</ul>
							</div>
						</div>
						<div className="col-12 col-md-6 col-lg-3">
							<div className={`persona-card company h-100 p-4 hover-lift scroll-fade-in ${workspaceVisible ? 'visible' : ''}`} style={{ opacity: workspaceVisible ? 1 : 0, transform: workspaceVisible ? 'translateY(0)' : 'translateY(30px)', transition: 'opacity 0.6s ease-out 0.2s, transform 0.6s ease-out 0.2s' }}>
								<div className="d-flex align-items-center gap-2 mb-3">
									<i className="bi bi-people-fill fs-4 text-success"></i>
									<span className="fw-semibold h5 mb-0">{copy('Company', 'Unternehmen', 'Tvrtka')}</span>
								</div>
								<p className="text-muted small mb-3">
									{copy('Manage teams, offers, and projects in one place.', 'Verwalten Sie Teams, Angebote und Projekte an einem Ort.', 'Upravljajte timovima, ponudama i projektima na jednom mjestu.')}
								</p>
								<ul className="list-unstyled small text-muted mb-0">
									<li className="d-flex align-items-center gap-2 mb-2">
										<i className="bi bi-check-circle-fill text-success"></i>
										{copy('Task board (progress, notes & attachments)', 'Aufgaben-Board (Fortschritt, Notizen & Anhänge)', 'Ploča zadataka (napredak, bilješke i privici)')}
									</li>
									<li className="d-flex align-items-center gap-2 mb-2">
										<i className="bi bi-check-circle-fill text-success"></i>
										{copy('Browse marketplace (available plans)', 'Marktplatz durchsuchen (verfügbare Pläne)', 'Pregledajte tržište (dostupni planovi)')}
									</li>
									<li className="d-flex align-items-center gap-2 mb-2">
										<i className="bi bi-check-circle-fill text-success"></i>
										{copy('Create quotes & send offers', 'Angebote erstellen & senden', 'Izradite ponude i pošaljite ih')}
									</li>
									<li className="d-flex align-items-center gap-2 mb-2">
										<i className="bi bi-check-circle-fill text-success"></i>
										{copy('Track projects & progress', 'Projekte & Fortschritt verfolgen', 'Pratite projekte i napredak')}
									</li>
									<li className="d-flex align-items-center gap-2 mb-2">
										<i className="bi bi-check-circle-fill text-success"></i>
										{copy('Schedule & calendar view', 'Plan- & Kalenderansicht', 'Raspored i prikaz kalendara')}
									</li>
									<li className="d-flex align-items-center gap-2 mb-2">
										<i className="bi bi-check-circle-fill text-success"></i>
										{copy('Team attendance & availability', 'Team-Anwesenheit & Verfügbarkeit', 'Prisutnost tima i dostupnost')}
									</li>
									<li className="d-flex align-items-center gap-2 mb-2">
										<i className="bi bi-check-circle-fill text-success"></i>
										{copy('Invite seekers to create plans', 'Suchende einladen, Pläne zu erstellen', 'Pozovite vlasnike vrta da izrade planove')}
									</li>
									<li className="d-flex align-items-center gap-2 mb-2">
										<i className="bi bi-check-circle-fill text-success"></i>
										{copy('Messages with seekers & team', 'Nachrichten mit Suchenden & Team', 'Poruke s vlasnicima i timom')}
									</li>
								</ul>
							</div>
						</div>
						<div className="col-12 col-md-6 col-lg-3">
							<div className={`persona-card gardener h-100 p-4 hover-lift scroll-fade-in ${workspaceVisible ? 'visible' : ''}`} style={{ opacity: workspaceVisible ? 1 : 0, transform: workspaceVisible ? 'translateY(0)' : 'translateY(30px)', transition: 'opacity 0.6s ease-out 0.3s, transform 0.6s ease-out 0.3s' }}>
								<div className="d-flex align-items-center gap-2 mb-3">
									<i className="bi bi-brightness-high fs-4 text-success"></i>
									<span className="fw-semibold h5 mb-0">{copy('Gardener', 'Gärtner', 'Vrtlar')}</span>
								</div>
								<p className="text-muted small mb-3">
									{copy('Browse plans, send quotes, and manage tasks and projects in the field.', 'Pläne durchsuchen, Angebote senden und Aufgaben sowie Projekte vor Ort verwalten.', 'Pregledajte planove, šaljite ponude i upravljajte zadacima i projektima na terenu.')}
								</p>
								<ul className="list-unstyled small text-muted mb-0">
									<li className="d-flex align-items-center gap-2 mb-2">
										<i className="bi bi-check-circle-fill text-success"></i>
										{copy('Task board (progress, notes & attachments)', 'Aufgaben-Board (Fortschritt, Notizen & Anhänge)', 'Ploča zadataka (napredak, bilješke i privici)')}
									</li>
									<li className="d-flex align-items-center gap-2 mb-2">
										<i className="bi bi-check-circle-fill text-success"></i>
										{copy('Browse marketplace (available plans)', 'Marktplatz durchsuchen (verfügbare Pläne)', 'Pregledajte tržište (dostupni planovi)')}
									</li>
									<li className="d-flex align-items-center gap-2 mb-2">
										<i className="bi bi-check-circle-fill text-success"></i>
										{copy('Create quotes & send offers', 'Angebote erstellen & senden', 'Izradite ponude i pošaljite ih')}
									</li>
									<li className="d-flex align-items-center gap-2 mb-2">
										<i className="bi bi-check-circle-fill text-success"></i>
										{copy('Track projects & progress', 'Projekte & Fortschritt verfolgen', 'Pratite projekte i napredak')}
									</li>
									<li className="d-flex align-items-center gap-2 mb-2">
										<i className="bi bi-check-circle-fill text-success"></i>
										{copy('Schedule & calendar view', 'Plan- & Kalenderansicht', 'Raspored i prikaz kalendara')}
									</li>
									<li className="d-flex align-items-center gap-2 mb-2">
										<i className="bi bi-check-circle-fill text-success"></i>
										{copy('Messages with seekers & team', 'Nachrichten mit Suchenden & Team', 'Poruke s vlasnicima i timom')}
									</li>
								</ul>
							</div>
						</div>
					</div>
				</div>
			</section>

			<section id="cta" className="landing-section py-5">
				<div className="container">
					<div className="cta-tile px-4 py-5 py-lg-5 shadow-lg position-relative overflow-hidden">
						<div className="row align-items-center g-4">
							<div className="col-lg-7">
								<h2 className="display-6 fw-bold text-success-emphasis mb-3">
									{t('home.cta.title', 'Build your dream garden')}
								</h2>
								<p className="text-muted mb-0">
									{t(
										'home.cta.subtitle',
										'Bring your garden vision to life with smart planning tools, expert guidance, and seamless project management.'
									)}
								</p>
							</div>
							<div className="col-lg-5 d-flex flex-column flex-sm-row gap-3 justify-content-end">
								<Link to={bottomCtaHref} className="btn btn-success btn-lg">
									{bottomCtaLabel}
								</Link>
								{user && (
									<Link to="/app/settings" className="btn btn-outline-success btn-lg">
										{t('home.cta.settings', 'Adjust settings')}
									</Link>
								)}
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Coming soon section – set SHOW_COMING_SOON to true to display */}
			{SHOW_COMING_SOON && (
			<section id="coming-soon" className="landing-section py-5 bg-light">
				<div className="container">
					<div className="text-center mb-4">
						<h2 className="display-6 text-success-emphasis mb-3">Coming soon</h2>
						<p className="text-muted lead fs-6">
							We're building features to help you maintain your garden and work smarter in the field.
						</p>
					</div>
					<div className="row g-4">
						<div className="col-12 col-md-6">
							<div className="coming-soon-card p-4 bg-white rounded-3 h-100 shadow-sm border border-success border-opacity-10">
								<h3 className="h5 fw-semibold text-success-emphasis mb-3">
									<i className="bi bi-house-heart me-2 text-success-emphasis"></i>
									For Garden Seekers
								</h3>
								<ul className="list-unstyled small text-muted mb-0">
									<li className="mb-2 d-flex align-items-start gap-2">
										<i className="bi bi-circle-fill text-success-emphasis mt-1" style={{ fontSize: '0.5rem', opacity: 0.4 }}></i>
										<span>Garden maintenance tracking (watering schedules, sensor monitoring)</span>
									</li>
									<li className="mb-2 d-flex align-items-start gap-2">
										<i className="bi bi-circle-fill text-success-emphasis mt-1" style={{ fontSize: '0.5rem', opacity: 0.4 }}></i>
										<span>Filter replacement reminders</span>
									</li>
									<li className="mb-2 d-flex align-items-start gap-2">
										<i className="bi bi-circle-fill text-success-emphasis mt-1" style={{ fontSize: '0.5rem', opacity: 0.4 }}></i>
										<span>Mobile-friendly maintenance calendar</span>
									</li>
								</ul>
							</div>
						</div>
						<div className="col-12 col-md-6">
							<div className="coming-soon-card p-4 bg-white rounded-3 h-100 shadow-sm border border-success border-opacity-10">
								<h3 className="h5 fw-semibold text-success-emphasis mb-3">
									<i className="bi bi-brightness-high me-2 text-success-emphasis"></i>
									For Gardeners
								</h3>
								<ul className="list-unstyled small text-muted mb-0">
									<li className="mb-2 d-flex align-items-start gap-2">
										<i className="bi bi-circle-fill text-success-emphasis mt-1" style={{ fontSize: '0.5rem', opacity: 0.4 }}></i>
										<span>Mobile task management for field work</span>
									</li>
									<li className="mb-2 d-flex align-items-start gap-2">
										<i className="bi bi-circle-fill text-success-emphasis mt-1" style={{ fontSize: '0.5rem', opacity: 0.4 }}></i>
										<span>Easy task organization and tracking</span>
									</li>
									<li className="mb-2 d-flex align-items-start gap-2">
										<i className="bi bi-circle-fill text-success-emphasis mt-1" style={{ fontSize: '0.5rem', opacity: 0.4 }}></i>
										<span>On-the-go calendar access</span>
									</li>
								</ul>
							</div>
						</div>
					</div>
				</div>
			</section>
			)}

			{/* Project Detail Modal */}
			{selectedProject && (
				<>
					<div 
						className="modal fade show" 
						style={{ display: 'block', zIndex: 30000 }} 
						tabIndex={-1}
						onClick={(e) => {
							// Close modal when clicking backdrop
							if (e.target === e.currentTarget) {
								handleCloseModal();
							}
						}}
					>
						<div 
							className="modal-dialog modal-xl modal-dialog-scrollable"
							onClick={(e) => e.stopPropagation()}
						>
							<div className="modal-content" onClick={(e) => e.stopPropagation()}>
								<div className="modal-header">
									<div className="d-flex align-items-center justify-content-between w-100">
										<h2 className="modal-title fw-bold text-success-emphasis mb-0">{selectedProject.title}</h2>
										<div className="d-flex gap-2 align-items-center">
											<button
												type="button"
												className="btn btn-outline-success btn-sm"
												onClick={handleGoToGallery}
											>
												<i className="bi bi-images me-1"></i>
												{t('public.nav.gallery', 'Gallery')}
											</button>
											<button
												type="button"
												className="btn-close"
												onClick={handleCloseModal}
												aria-label={t('gallery.modal.close', 'Close')}
											></button>
										</div>
									</div>
								</div>
								<div className="modal-body">
									<div className="mb-4">
										<p className="lead text-muted mb-4">{selectedProject.description}</p>
										<div className="row g-4 mb-4">
											<div className="col-12 col-lg-6">
												<div className="position-relative" style={{ aspectRatio: '16/9', overflow: 'hidden', borderRadius: '1rem' }}>
													<span className="badge bg-dark position-absolute top-0 start-0 m-3" style={{ zIndex: 1 }}>
														{t('home.showcase.before', 'Before')}
													</span>
													<img
														src={selectedProject.before}
														alt={`${selectedProject.title} before`}
														className="w-100 h-100"
														style={{ objectFit: 'cover', borderRadius: '1rem' }}
														onError={(e) => {
															console.error('Failed to load image:', selectedProject.before);
														}}
													/>
												</div>
											</div>
											<div className="col-12 col-lg-6">
												<div className="position-relative" style={{ aspectRatio: '16/9', overflow: 'hidden', borderRadius: '1rem' }}>
													<span className="badge bg-success position-absolute top-0 start-0 m-3" style={{ zIndex: 1 }}>
														{t('home.showcase.after', 'After')}
													</span>
													<img
														src={selectedProject.after}
														alt={`${selectedProject.title} after`}
														className="w-100 h-100"
														style={{ objectFit: 'cover', borderRadius: '1rem' }}
														onError={(e) => {
															console.error('Failed to load image:', selectedProject.after);
														}}
													/>
												</div>
											</div>
										</div>
									</div>

									<div className="row g-4">
										<div className="col-12 col-lg-8">
											<div className="card border-0 shadow-sm mb-4">
												<div className="card-body p-4">
													<h3 className="h5 fw-bold text-success-emphasis mb-4">{t('gallery.modal.details', 'Project Details')}</h3>
													<div className="row g-3 mb-4">
														<div className="col-6 col-md-3">
															<div className="text-muted small">{t('gallery.modal.location', 'Location')}</div>
															<div className="fw-semibold">{selectedProject.location}</div>
														</div>
														<div className="col-6 col-md-3">
															<div className="text-muted small">{t('gallery.modal.area', 'Area')}</div>
															<div className="fw-semibold">{selectedProject.details.area}</div>
														</div>
														<div className="col-6 col-md-3">
															<div className="text-muted small">{t('gallery.modal.duration', 'Duration')}</div>
															<div className="fw-semibold">{selectedProject.details.duration}</div>
														</div>
														<div className="col-6 col-md-3">
															<div className="text-muted small">{t('gallery.modal.budget', 'Budget')}</div>
															<div className="fw-semibold">{selectedProject.details.budget}</div>
														</div>
													</div>
													<div className="mb-4">
														<h4 className="h6 fw-semibold mb-3">{t('gallery.modal.keyFeatures', 'Key Features')}</h4>
														<ul className="list-unstyled">
															{selectedProject.details.features.map((feature, idx) => (
																<li key={idx} className="mb-2 d-flex align-items-start gap-2">
																	<i className="bi bi-check-circle-fill text-success mt-1"></i>
																	<span>{feature}</span>
																</li>
															))}
														</ul>
													</div>
													<div className="mb-4">
														<h4 className="h6 fw-semibold mb-3">{t('gallery.modal.plantSelection', 'Plant Selection')}</h4>
														<div className="row g-2">
															{selectedProject.details.plants.map((plant, idx) => (
																<div key={idx} className="col-12 col-md-6">
																	<div className="d-flex align-items-center gap-2">
																		<i className="bi bi-flower1 text-success"></i>
																		<span className="small">{plant}</span>
																	</div>
																</div>
															))}
														</div>
													</div>
												</div>
											</div>

											<div className="card border-0 shadow-sm">
												<div className="card-body p-4">
													<h3 className="h5 fw-bold text-success-emphasis mb-4">{t('gallery.modal.story', 'Project Story')}</h3>
													<div className="mb-4">
														<h4 className="h6 fw-semibold mb-3 text-warning">{t('gallery.modal.challenges', 'Challenges')}</h4>
														<ul className="list-unstyled">
															{selectedProject.details.challenges.map((challenge, idx) => (
																<li key={idx} className="mb-2 d-flex align-items-start gap-2">
																	<i className="bi bi-exclamation-triangle-fill text-warning mt-1"></i>
																	<span>{challenge}</span>
																</li>
															))}
														</ul>
													</div>
													<div>
														<h4 className="h6 fw-semibold mb-3 text-success">{t('gallery.modal.solutions', 'Solutions')}</h4>
														<ul className="list-unstyled">
															{selectedProject.details.solutions.map((solution, idx) => (
																<li key={idx} className="mb-2 d-flex align-items-start gap-2">
																	<i className="bi bi-lightbulb-fill text-success mt-1"></i>
																	<span>{solution}</span>
																</li>
															))}
														</ul>
													</div>
												</div>
											</div>
										</div>

										<div className="col-12 col-lg-4">
											<div className="card border-0 shadow-sm bg-success-subtle">
												<div className="card-body p-4">
													<h3 className="h5 fw-bold text-success-emphasis mb-3">{t('gallery.modal.testimonial', 'Client Testimonial')}</h3>
													<blockquote className="mb-0">
														<p className="text-muted mb-3">"{selectedProject.details.testimonial}"</p>
														<footer className="small text-muted">
															— {selectedProject.client}
														</footer>
													</blockquote>
												</div>
											</div>
											<div className="card border-0 shadow-sm mt-4">
												<div className="card-body p-4">
													<h3 className="h5 fw-bold text-success-emphasis mb-3">{t('gallery.modal.info', 'Project Info')}</h3>
													<div className="small">
														<div className="mb-2">
															<span className="text-muted">{t('gallery.modal.client', 'Client')}:</span>
															<div className="fw-semibold">{selectedProject.client}</div>
														</div>
														<div className="mb-2">
															<span className="text-muted">{t('gallery.modal.completed', 'Completed')}:</span>
															<div className="fw-semibold">{new Date(selectedProject.completedDate).toLocaleDateString(localeByLanguage[language], { year: 'numeric', month: 'long', day: 'numeric' })}</div>
														</div>
														<div className="mb-2">
															<span className="text-muted">{t('gallery.modal.style', 'Style')}:</span>
															<div className="fw-semibold">{selectedProject.style}</div>
														</div>
													</div>
												</div>
											</div>
											<Link to={primaryCtaHref} className="btn btn-success w-100 mt-4" onClick={handleCloseModal}>
												{primaryCtaLabel}
											</Link>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div 
						className="modal-backdrop fade show" 
						onClick={handleCloseModal}
						style={{ opacity: 0.25, zIndex: 29900 }}
					></div>
				</>
			)}
		</>
	);
}


